import XIcon from '@/public/icons/close_black.svg';
import ErrorIcon from '@/public/icons/exclamation_circle.svg';
import SuccessIcon from '@/public/icons/check_circle.svg';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import React, { useRef, useState, useMemo, useEffect } from 'react';
import {
  useReviewTelecomSupportDocument,
  userGetPhoneBillStatus2,
  useSubmitTelecomSupportClaim,
  useUploadFile,
} from '@/hooks/apiHooks';
import {
  StyledDialog,
  Header,
  HeaderTitle,
  CloseButton,
  Body,
  ContentLayout,
  StyledTitle,
  StyledContent,
  EvidenceDescription,
  ActionRow,
  CancelButton,
  SubmitButton,
  LayoutUpload,
  UploadLeftSection,
  UploadRightSection,
  FileSize,
  ReasonContainer,
  ReasonText,
  StyledXButton,
} from './styles';
import { PhoneBillResponse } from '@/types/api';
import { formatBytes } from '@/components/Record/util/function';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import CustomUpload from '@/components/CustomUpload';
import { isImageFile } from '@/components/AttachmentFile/utils';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import PasswordPromptDialog from './PasswordPromptDialog';
import {
  inspectPdfPassword,
  tryDecryptPdfWithPassword,
} from '@/utils/pdf/decryptPdf';

type P = {
  open: boolean;
  handleClose: () => void;
};

const PhoneBillDialog = ({ open, handleClose }: P) => {
  const toast = useSnackbar();

  const today = dayjs();
  const todayWord = today.format('YYYY년 M월');
  const year = today.year();
  const month = today.month() + 1;

  const [isChecking, setIsChecking] = useState(false);

  const checkStatus = async () => {
    setIsChecking(true);
    try {
      const data = await userGetPhoneBillStatus2({ year: year, month: month });

      if (data !== null && data?.status === 'submitted') {
        dialog.show({
          type: 'alert',
          message: `이번 달(${todayWord})\n통신비 영수증 제출을 이미 완료했습니다.`,
          confirmButtonText: '확인',
          onAfterClose: () => handleClose(),
        });
      }
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 403) {
        dialog.show({
          type: 'alert',
          message: e.response.data?.message || '통신비 지급 대상자가 아닙니다.',
          confirmButtonText: '확인',
          onAfterClose: () => handleClose(),
        });
      }
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    open && checkStatus();
  }, [open]);

  const [responseResult, setResponseResult] = useState<
    (PhoneBillResponse & { uploadFile: File }) | undefined
  >(undefined);
  const { divisionName, departmentName, name, phoneMobile, employeeNumber } =
    useAtomValue(MyInfoState);
  const dialog = useDialog();
  const [uploading, setUploading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const previewUrl = useMemo(
    () =>
      responseResult
        ? URL.createObjectURL(responseResult.uploadFile)
        : undefined,
    [responseResult],
  );

  const [pendingPdfFile, setPendingPdfFile] = useState<File | null>(null);
  const [passwordChecking, setPasswordChecking] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const uploadAndReview = async (file: File) => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setUploading(true);
    try {
      const uploadRes = await useUploadFile(file, true, controller.signal);
      const { uuid } = uploadRes.data;

      const reviewRes = await useReviewTelecomSupportDocument(
        uuid,
        file.type,
        year,
        month,
        controller.signal,
      );
      setResponseResult({ ...reviewRes.data, uploadFile: file });
    } catch (e) {
      setResponseResult(undefined);
      if (axios.isAxiosError(e) && e.response) {
        const message =
          e.response.data?.message ?? '잠시 후 다시 시도해 주십시오.';
        toast.show({ severity: 'error', message, duration: 3000 });
      }
    } finally {
      setUploading(false);
      abortControllerRef.current = null;
    }
  };

  const handleFile = async (file: File) => {
    setPendingPdfFile(null);
    setPasswordError(null);
    setPasswordChecking(false);
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      dialog.show({
        type: 'alert',
        message: '지원하지 않는 형식입니다.',
        confirmButtonText: '확인',
      });
      return;
    }

    if (file.type === 'application/pdf') {
      setUploading(true);
      try {
        const inspection = await inspectPdfPassword(file);
        if (inspection.kind === 'needs-password') {
          setPendingPdfFile(file);
          setPasswordError(null);
          setUploading(false);
          return;
        }
        if (inspection.kind === 'corrupt') {
          setUploading(false);
          dialog.show({
            type: 'alert',
            message: 'PDF 파일을 읽을 수 없습니다.',
            confirmButtonText: '확인',
          });
          return;
        }
      } catch {
        setUploading(false);
        dialog.show({
          type: 'alert',
          message: 'PDF 파일을 읽을 수 없습니다.',
          confirmButtonText: '확인',
        });
        return;
      }
      setUploading(false);
    }

    await uploadAndReview(file);
  };

  const handlePasswordSubmit = async (password: string) => {
    if (!pendingPdfFile) return;
    setPasswordChecking(true);
    setPasswordError(null);
    try {
      const result = await tryDecryptPdfWithPassword(pendingPdfFile, password);
      if (!result.ok) {
        if (result.reason === 'incorrect') {
          setPasswordError('암호가 일치하지 않습니다. 다시 입력해 주세요.');
        } else {
          setPasswordError('PDF 파일을 처리할 수 없습니다.');
        }
        return;
      }
      const decrypted = result.file;
      setPendingPdfFile(null);
      setPasswordError(null);
      await uploadAndReview(decrypted);
    } finally {
      setPasswordChecking(false);
    }
  };

  const handlePasswordCancel = () => {
    if (passwordChecking) return;
    setPendingPdfFile(null);
    setPasswordError(null);
  };

  const handleCancelUpload = () => {
    abortControllerRef.current?.abort();
  };

  const handleSubmit = async () => {
    if (!responseResult) return;
    try {
      await useSubmitTelecomSupportClaim(responseResult.id);
      toast.show({
        severity: 'success',
        message: '통신비 영수증이 제출되었습니다.',
        duration: 3000,
      });
      setResponseResult(undefined);
      handleClose();
    } catch (e) {
      const message =
        axios.isAxiosError(e) && e.response?.data?.message
          ? e.response.data.message
          : '잠시 후 다시 시도해 주십시오.';

      dialog.show({
        type: 'alert',
        message,
        confirmButtonText: '확인',
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <StyledDialog
      maxWidth={'sm'}
      fullWidth={true}
      open={open}
      onClose={handleClose}
    >
      <div style={{ position: 'relative' }}>
        {isChecking && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.7)',
            }}
          >
            <Spinner theme={'black'} />
          </div>
        )}
        <Header>
          <HeaderTitle>통신비 영수증 제출</HeaderTitle>
          <CloseButton onClick={handleClose}>
            <XIcon width={24} height={24} />
          </CloseButton>
        </Header>
        <Body>
          <ContentLayout>
            <StyledTitle>신청연월</StyledTitle>
            <StyledContent>{todayWord}</StyledContent>
          </ContentLayout>
          <ContentLayout>
            <StyledTitle>신청자</StyledTitle>
            <StyledContent>{`${divisionName ?? ''} ${departmentName ?? ''} ${
              name ?? ''
            }`}</StyledContent>
          </ContentLayout>
          <ContentLayout>
            <StyledTitle>사번</StyledTitle>
            <StyledContent>{employeeNumber}</StyledContent>
          </ContentLayout>
          <ContentLayout>
            <StyledTitle>휴대폰 번호</StyledTitle>
            <StyledContent>{phoneMobile ?? ''}</StyledContent>
          </ContentLayout>
          <ContentLayout>
            <StyledTitle>증빙자료</StyledTitle>
            <EvidenceDescription>
              통신비 지원을 위해 아래 항목이 포함된 증빙을 첨부해 주세요.
              <br /> · 필수포함 정보: 이름 or 휴대폰 번호, 실사용기간, 납부 금액
            </EvidenceDescription>
          </ContentLayout>
          {responseResult ? (
            <div>
              <LayoutUpload>
                <UploadLeftSection>
                  <img
                    src={
                      isImageFile(responseResult.uploadFile.name)
                        ? previewUrl
                        : '/icons/files/Type=pdf@2x.png'
                    }
                    width={40}
                    height={40}
                    alt="preview"
                    style={{ borderRadius: '2px' }}
                  />
                  <div>{responseResult.uploadFile.name}</div>
                </UploadLeftSection>
                <UploadRightSection>
                  <FileSize>
                    {formatBytes(responseResult.uploadFile.size)}
                  </FileSize>
                  <StyledXButton onClick={() => setResponseResult(undefined)}>
                    <XIcon width={16} height={16} />
                  </StyledXButton>
                </UploadRightSection>
              </LayoutUpload>
              <ReasonContainer>
                {responseResult.nameNumberMatch ? (
                  <ReasonText>
                    <SuccessIcon />
                    이름 or 휴대폰 번호
                  </ReasonText>
                ) : (
                  <ReasonText $failed>
                    <ErrorIcon />
                    이름 or 휴대폰 번호: 신청자 이름 혹은 번호와 일치하지
                    않아요.
                  </ReasonText>
                )}
                {responseResult.actualUsagePeriodMatch ? (
                  <ReasonText>
                    <SuccessIcon />
                    실사용기간
                  </ReasonText>
                ) : (
                  <ReasonText $failed>
                    <ErrorIcon />
                    {`사용기간 ${today
                      .subtract(1, 'month')
                      .startOf('month')
                      .format('YYYY-MM-DD')}~${today
                      .subtract(1, 'month')
                      .endOf('month')
                      .format('YYYY-MM-DD')}인 영수증을 첨부해주세요.`}
                  </ReasonText>
                )}
                {responseResult.reimbursementAmount ? (
                  <ReasonText>
                    <SuccessIcon />
                    납부 금액
                  </ReasonText>
                ) : (
                  <ReasonText $failed>
                    <ErrorIcon />
                    납부 금액: 금액이 포함되지 않았어요.
                  </ReasonText>
                )}
              </ReasonContainer>
            </div>
          ) : (
            <CustomUpload
              uploading={uploading}
              handleCancel={handleCancelUpload}
              handleInputChange={handleInputChange}
              handleDrop={handleDrop}
            />
          )}
          <ActionRow>
            <CancelButton onClick={handleClose}>닫기</CancelButton>
            <SubmitButton
              disabled={responseResult?.status !== 'review_success'}
              onClick={handleSubmit}
            >
              제출
            </SubmitButton>
          </ActionRow>
        </Body>
      </div>
      <PasswordPromptDialog
        open={!!pendingPdfFile}
        fileName={pendingPdfFile?.name ?? ''}
        checking={passwordChecking}
        error={passwordError}
        onClose={handlePasswordCancel}
        onSubmit={handlePasswordSubmit}
      />
    </StyledDialog>
  );
};

export default PhoneBillDialog;
